import type { StepDef } from './runner';
import type { DecodedNewRow, DecodedUpdateRow } from './types';

/**
 * The engine playbook for a `new` row: create the site, then write its
 * geography, then set its activity. Each step waits on the previous one
 * because the latter two need the site UUID returned by createSite.
 *
 * On retry, the harness skips steps whose status is already `done`. The
 * site step also short-circuits when `state.siteId` is already populated,
 * making the UUID round-trip work even if the retry seed didn't mark site
 * as done.
 */
export const newRowSteps: StepDef<DecodedNewRow>[] = [
    {
        name: 'site',
        shouldRun: (_, state) => !state.siteId,
        run: async (row, _, mutators) => {
            const { id } = await mutators.createSite(row.site);
            return { siteId: id };
        },
    },
    {
        name: 'geography',
        shouldRun: () => true,
        run: async (row, state, mutators) => {
            await mutators.createGeography(state.siteId!, row.geography);
            return {};
        },
    },
    {
        name: 'activity',
        shouldRun: () => true,
        run: async (row, state, mutators) => {
            await mutators.setActivity(state.siteId!, row.activity);
            return {};
        },
    },
];

/**
 * The engine playbook for an `update` row: only the writes whose sub-object
 * is present on the decoded row fire. `decode()` upstream is responsible for
 * including each sub-object iff the corresponding fields appear in the
 * engine's `changes` list — pipelines here just consume that signal.
 */
export const updateRowSteps: StepDef<DecodedUpdateRow>[] = [
    {
        name: 'site',
        shouldRun: row => row.site !== undefined,
        run: async (row, _, mutators) => {
            await mutators.updateSite(row.internalId, row.site!);
            return {};
        },
    },
    {
        name: 'geography',
        shouldRun: row => row.geography !== undefined,
        run: async (row, _, mutators) => {
            await mutators.createGeography(row.internalId, row.geography!);
            return {};
        },
    },
    {
        name: 'activity',
        shouldRun: row => row.activity !== undefined,
        run: async (row, _, mutators) => {
            await mutators.setActivity(row.internalId, row.activity!);
            return {};
        },
    },
];
